import { add, isBefore, max } from 'date-fns'

import { Outlet } from '@src/graphql-types'
import {
  FulfilmentFilterWhenASAP,
  FulfilmentFilterWhenAnytime,
  FulfilmentFilterWhenPreorder,
  FulfilmentFilterWhenType,
} from '@src/hooks/useFulfilmentFilter/validation'
import { DateifyOutlet } from '@src/utils/fulfilmentTimes/parsers'
import { ParsedDeliveryPreorderWindow } from '@src/utils/fulfilmentTimes/types'
import { OrderabilityFragment } from '../../../../fragments/__generated__/OrderabilityFragment'

// calculates the delivery preorder window from the current fulfilment filter
// (attempts to find the first deliverable window today or on the date selected by the customer)
// returns null if it should be asap or there are no deliverable windows
export const calculateDeliveryPreorderWindow = ({
  outlet,
  fulfilmentFilterWhen,
}: {
  outlet: DateifyOutlet<
    Pick<Outlet, 'prepTime' | 'deliveryPreorderWindows' | 'closedUntil'> & {
      orderability: OrderabilityFragment
    }
  >
  fulfilmentFilterWhen:
    | Zod.infer<typeof FulfilmentFilterWhenAnytime>
    | Zod.infer<typeof FulfilmentFilterWhenASAP>
    | Zod.infer<typeof FulfilmentFilterWhenPreorder>
}): ParsedDeliveryPreorderWindow | null => {
  if (
    // customer selected preorder and outlet offers preorders
    (fulfilmentFilterWhen.type === FulfilmentFilterWhenType.PREORDER &&
      outlet.orderability.delivery.preorder.isAvailableForSelectedDate) ||
    // customer selected ANYTIME or ASAP but asap is not available
    ([FulfilmentFilterWhenType.ANYTIME, FulfilmentFilterWhenType.ASAP].includes(
      fulfilmentFilterWhen.type
    ) &&
      !outlet.orderability.delivery.asap.isAvailableNow)
  ) {
    // calculate best preorder window based on customer's selected preorder date / now + prep
    const earliestOpenTime = outlet.closedUntil
      ? max([new Date(), outlet.closedUntil])
      : new Date()
    const earliestPreorderTime = add(earliestOpenTime, {
      minutes: outlet.prepTime,
    })
    const earliestPreorderTimeOrSelectedPreorderTime =
      fulfilmentFilterWhen.type === FulfilmentFilterWhenType.PREORDER
        ? max([fulfilmentFilterWhen.preorderDate, earliestPreorderTime])
        : earliestPreorderTime

    // find the earliest window that has a `start` after the earliest preorder time
    return outlet.deliveryPreorderWindows.reduce<ParsedDeliveryPreorderWindow | null>(
      (acc, deliveryWindow) => {
        if (
          !deliveryWindow.isFull &&
          !isBefore(
            deliveryWindow.start,
            earliestPreorderTimeOrSelectedPreorderTime
          ) &&
          (!acc || isBefore(deliveryWindow.start, acc.start))
        ) {
          return deliveryWindow
        }
        return acc
      },
      null
    )
  }

  // default to asap
  return null
}
